import { supabase } from '../lib/supabase';

export const debtPayoffService = {
  // Get all debts for a user
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('debt_payoff_tracker')
        ?.select(`
          *,
          accounts (
            id,
            name,
            type,
            balance
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => {
        // Use linked account balance if available, otherwise use stored current_balance
        const currentBalance = row?.accounts?.balance 
          ? Math.abs(parseFloat(row?.accounts?.balance))
          : parseFloat(row?.current_balance);

        return {
          id: row?.id,
          userId: row?.user_id,
          linkedAccountId: row?.linked_account_id,
          debtName: row?.debt_name,
          principalAmount: parseFloat(row?.principal_amount),
          currentBalance: currentBalance,
          interestRate: parseFloat(row?.interest_rate),
          minimumPayment: parseFloat(row?.minimum_payment),
          paymentFrequency: row?.payment_frequency,
          additionalFees: parseFloat(row?.additional_fees),
          insuranceFee: parseFloat(row?.insurance_fee),
          payoffStrategy: row?.payoff_strategy,
          optimalPayment: row?.optimal_payment ? parseFloat(row?.optimal_payment) : null,
          aiRecommendations: row?.ai_recommendations,
          status: row?.status,
          targetPayoffDate: row?.target_payoff_date,
          startDate: row?.start_date,
          endDate: row?.end_date,
          termLength: row?.term_length,
          createdAt: row?.created_at,
          updatedAt: row?.updated_at,
          linkedAccount: row?.accounts ? {
            id: row?.accounts?.id,
            name: row?.accounts?.name,
            type: row?.accounts?.type,
            balance: parseFloat(row?.accounts?.balance)
          } : null
        };
      });
    } catch (error) {
      console.error('Error fetching debts:', error);
      throw error;
    }
  },

  // Get debt portfolio summary
  async getPortfolioSummary(userId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_debt_portfolio_summary', { p_user_id: userId });

      if (error) throw error;

      const summary = data?.[0] || {};
      return {
        totalDebt: parseFloat(summary?.total_debt || 0),
        totalMinimumPayment: parseFloat(summary?.total_minimum_payment || 0),
        weightedAvgInterestRate: parseFloat(summary?.weighted_avg_interest_rate || 0),
        totalMonthlyInterest: parseFloat(summary?.total_monthly_interest || 0),
        activeDebtCount: parseInt(summary?.active_debt_count || 0)
      };
    } catch (error) {
      console.error('Error fetching portfolio summary:', error);
      throw error;
    }
  },

  // Create new debt
  async create(debtData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('debt_payoff_tracker')
        ?.insert({
          user_id: user?.id,
          linked_account_id: debtData?.linkedAccountId || null,
          debt_name: debtData?.debtName,
          principal_amount: debtData?.principalAmount,
          current_balance: debtData?.currentBalance,
          interest_rate: debtData?.interestRate,
          minimum_payment: debtData?.minimumPayment,
          payment_frequency: debtData?.paymentFrequency || 'monthly',
          additional_fees: debtData?.additionalFees || 0,
          insurance_fee: debtData?.insuranceFee || 0,
          payoff_strategy: debtData?.payoffStrategy || 'avalanche',
          target_payoff_date: debtData?.targetPayoffDate || null,
          start_date: debtData?.startDate || null,
          end_date: debtData?.endDate || null,
          term_length: debtData?.termLength || null
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        linkedAccountId: data?.linked_account_id,
        debtName: data?.debt_name,
        principalAmount: parseFloat(data?.principal_amount),
        currentBalance: parseFloat(data?.current_balance),
        interestRate: parseFloat(data?.interest_rate),
        minimumPayment: parseFloat(data?.minimum_payment),
        paymentFrequency: data?.payment_frequency,
        additionalFees: parseFloat(data?.additional_fees),
        insuranceFee: parseFloat(data?.insurance_fee),
        payoffStrategy: data?.payoff_strategy,
        status: data?.status,
        targetPayoffDate: data?.target_payoff_date,
        startDate: data?.start_date,
        endDate: data?.end_date,
        termLength: data?.term_length,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating debt:', error);
      throw error;
    }
  },

  // Update debt
  async update(debtId, updates) {
    try {
      const updateData = {};
      if (updates?.linkedAccountId !== undefined) updateData.linked_account_id = updates?.linkedAccountId;
      if (updates?.debtName !== undefined) updateData.debt_name = updates?.debtName;
      if (updates?.currentBalance !== undefined) updateData.current_balance = updates?.currentBalance;
      if (updates?.interestRate !== undefined) updateData.interest_rate = updates?.interestRate;
      if (updates?.minimumPayment !== undefined) updateData.minimum_payment = updates?.minimumPayment;
      if (updates?.paymentFrequency !== undefined) updateData.payment_frequency = updates?.paymentFrequency;
      if (updates?.additionalFees !== undefined) updateData.additional_fees = updates?.additionalFees;
      if (updates?.insuranceFee !== undefined) updateData.insurance_fee = updates?.insuranceFee;
      if (updates?.payoffStrategy !== undefined) updateData.payoff_strategy = updates?.payoffStrategy;
      if (updates?.optimalPayment !== undefined) updateData.optimal_payment = updates?.optimalPayment;
      if (updates?.aiRecommendations !== undefined) updateData.ai_recommendations = updates?.aiRecommendations;
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.targetPayoffDate !== undefined) updateData.target_payoff_date = updates?.targetPayoffDate;
      if (updates?.startDate !== undefined) updateData.start_date = updates?.startDate;
      if (updates?.endDate !== undefined) updateData.end_date = updates?.endDate;
      if (updates?.termLength !== undefined) updateData.term_length = updates?.termLength;

      const { data, error } = await supabase
        ?.from('debt_payoff_tracker')
        ?.update(updateData)
        ?.eq('id', debtId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        linkedAccountId: data?.linked_account_id,
        debtName: data?.debt_name,
        principalAmount: parseFloat(data?.principal_amount),
        currentBalance: parseFloat(data?.current_balance),
        interestRate: parseFloat(data?.interest_rate),
        minimumPayment: parseFloat(data?.minimum_payment),
        paymentFrequency: data?.payment_frequency,
        additionalFees: parseFloat(data?.additional_fees),
        insuranceFee: parseFloat(data?.insurance_fee),
        payoffStrategy: data?.payoff_strategy,
        optimalPayment: data?.optimal_payment ? parseFloat(data?.optimal_payment) : null,
        aiRecommendations: data?.ai_recommendations,
        status: data?.status,
        targetPayoffDate: data?.target_payoff_date,
        startDate: data?.start_date,
        endDate: data?.end_date,
        termLength: data?.term_length,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating debt:', error);
      throw error;
    }
  },

  // Delete debt
  async delete(debtId) {
    try {
      const { error } = await supabase
        ?.from('debt_payoff_tracker')
        ?.delete()
        ?.eq('id', debtId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting debt:', error);
      throw error;
    }
  },

  // Get payment history for a debt (includes automatic transaction-based payments)
  async getPaymentHistory(debtId) {
    try {
      const { data, error } = await supabase
        ?.from('debt_payment_history')
        ?.select(`
          *,
          transactions (
            id,
            description,
            date,
            type
          )
        `)
        ?.eq('debt_id', debtId)
        ?.order('payment_date', { ascending: false });

      if (error) throw error;

      return data?.map(payment => ({
        id: payment?.id,
        debtId: payment?.debt_id,
        paymentDate: payment?.payment_date,
        paymentAmount: parseFloat(payment?.payment_amount),
        principalPaid: parseFloat(payment?.principal_paid),
        interestPaid: parseFloat(payment?.interest_paid),
        feesPaid: parseFloat(payment?.fees_paid),
        remainingBalance: parseFloat(payment?.remaining_balance),
        notes: payment?.notes,
        transactionId: payment?.transaction_id,
        isAutomatic: payment?.is_automatic,
        transaction: payment?.transactions ? {
          id: payment?.transactions?.id,
          description: payment?.transactions?.description,
          date: payment?.transactions?.date,
          type: payment?.transactions?.type
        } : null,
        createdAt: payment?.created_at
      }));
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  },

  // Add payment to debt
  async addPayment(paymentData) {
    try {
      const { data, error } = await supabase
        ?.from('debt_payment_history')
        ?.insert({
          debt_id: paymentData?.debtId,
          payment_date: paymentData?.paymentDate,
          payment_amount: paymentData?.paymentAmount,
          principal_paid: paymentData?.principalPaid,
          interest_paid: paymentData?.interestPaid,
          fees_paid: paymentData?.feesPaid || 0,
          remaining_balance: paymentData?.remainingBalance,
          notes: paymentData?.notes || null
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        debtId: data?.debt_id,
        paymentDate: data?.payment_date,
        paymentAmount: parseFloat(data?.payment_amount),
        principalPaid: parseFloat(data?.principal_paid),
        interestPaid: parseFloat(data?.interest_paid),
        feesPaid: parseFloat(data?.fees_paid),
        remainingBalance: parseFloat(data?.remaining_balance),
        notes: data?.notes,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  }
};